/**
 * Mix a somma 100 della scheda di libreria (V64), raggruppato per famiglia:
 * ogni famiglia con la sua icona e la somma delle sue quote, ogni attributo
 * con un cursore da 0 a 100 a passi di 5. Ci sono tutte le famiglie, portiere
 * compreso: una scheda la svolge chiunque. Una quota già salvata fuori dai
 * passi resta com'è finché non si tocca. Il totale lo mostra chi lo contiene.
 */

import { useMemo } from 'react';
import { ETICHETTA_FAMIGLIA_ATTRIBUTO, FAMIGLIE_ATTRIBUTO } from '@calcio/shared';
import { FieldDescription, FieldLegend, FieldSet } from '@/components/ui/field';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { mixDaQuote, type AttributoPerMix } from './schede.js';

export function EditorMix({
  attributi,
  quote,
  onQuota,
  idPrefisso = 'mix',
}: {
  attributi: readonly AttributoPerMix[];
  quote: Record<string, string>;
  onQuota: (attributoId: string, valore: string) => void;
  idPrefisso?: string;
}) {
  const { mix } = useMemo(() => mixDaQuote(attributi, quote), [attributi, quote]);
  const perFamiglia = FAMIGLIE_ATTRIBUTO.map((famiglia) => {
    const voci = attributi.filter((a) => a.famiglia === famiglia);
    const totale = mix
      .filter((v) => voci.some((a) => a.id === v.attributoId))
      .reduce((t, v) => t + v.quota, 0);
    return { famiglia, voci, totale };
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium">Mix</h3>
        <FieldDescription>Quote a passi di 5; a zero l’attributo resta fuori.</FieldDescription>
      </div>
      {perFamiglia.map(
        ({ famiglia, voci, totale }) =>
          voci.length > 0 && (
            // Il riquadro sta fuori dal fieldset, che taglierebbe il bordo con la legenda.
            <div key={famiglia} className="rounded-lg border border-border p-3">
              <FieldSet>
                <FieldLegend className="flex w-full items-center gap-2">
                  <IconaFamiglia famiglia={famiglia} className="size-4" />
                  <span className="flex-1">{ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}</span>
                  {totale > 0 && (
                    <span className="text-muted-foreground tabular-nums">{totale}</span>
                  )}
                </FieldLegend>
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  {voci.map((a) => {
                    const quota = Number(quote[a.id]) || 0;
                    const etichetta = `${idPrefisso}-${a.id}`;
                    return (
                      // Il cursore di Radix non prende l'etichetta dalla radice: la dà il gruppo.
                      <div
                        key={a.id}
                        role="group"
                        aria-labelledby={etichetta}
                        className="flex flex-col gap-2"
                      >
                        <div className="flex items-baseline gap-2">
                          <span
                            id={etichetta}
                            className={cn(
                              'min-w-0 flex-1 truncate',
                              quota === 0 && 'text-muted-foreground',
                            )}
                          >
                            {a.nome}
                          </span>
                          <span className="w-8 text-right font-medium tabular-nums">
                            {quota || '—'}
                          </span>
                        </div>
                        <Slider
                          value={[quota]}
                          min={0}
                          max={100}
                          step={5}
                          onValueChange={([v]) => onQuota(a.id, v ? String(v) : '')}
                        />
                      </div>
                    );
                  })}
                </div>
              </FieldSet>
            </div>
          ),
      )}
    </div>
  );
}
